import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import Layout from "@/components/Layout";
import { useAuth } from "@/lib/auth";
import { FileText, ArrowRight, Download } from "lucide-react";
import { PDFDownloadLink } from "@react-pdf/renderer";
import { EventDocPDF } from "@/pdf/EventDocPDF";

const KIND_LABEL = {
  guide: "Guide",
  agenda: "Agenda",
  faq: "FAQ",
  info: "Fact Sheet",
};

/**
 * Event Documents index. Auth-gated (Prime Suppliers, SBEs, T+L Attendees,
 * Speakers, and Admins all see this).
 */
export default function EventDocuments() {
  const { user, loading: authLoading } = useAuth();
  const nav = useNavigate();
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { nav("/login?next=/documents"); return; }
    api.get("/event-docs")
      .then(({ data }) => setDocs(data.documents || []))
      .finally(() => setLoading(false));
  }, [user, authLoading, nav]);

  if (authLoading || loading) {
    return <Layout><section className="container-summit py-24"><div className="overline">Loading documents…</div></section></Layout>;
  }

  return (
    <Layout>
      {/* Hero */}
      <section className="bg-[#0A0A0A] text-white">
        <div className="container-summit py-14 md:py-20">
          <div className="overline text-white/60"><span className="accent-rule" />Event Documents</div>
          <h1 className="serif text-3xl sm:text-4xl md:text-5xl mt-4 leading-[1.1]" data-testid="documents-title">
            Plan Your Visit
          </h1>
          <p className="mt-5 text-white/70 max-w-2xl text-base md:text-lg leading-relaxed">
            Find everything you need to prepare for the summit, including the agenda, venue information, arrival instructions, and event guides.
          </p>
        </div>
      </section>

      {/* Grid */}
      <section className="container-summit py-14">
        {docs.length === 0 ? (
          <div className="text-sm text-zinc-500">No published documents yet.</div>
        ) : (
          <div className="grid gap-5 md:grid-cols-2">
            {docs.map((d) => (
              <div key={d.slug} className="group border border-black bg-white p-7 flex flex-col hover:bg-black hover:text-white transition-colors" data-testid={`doc-card-${d.slug}`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="h-11 w-11 border border-current flex items-center justify-center">
                    <FileText size={18} strokeWidth={1.4} />
                  </div>
                  <div className="text-[10px] uppercase tracking-[0.22em] opacity-60">
                    {KIND_LABEL[d.kind] || "Document"}
                  </div>
                </div>
                <div className="mt-6 serif text-3xl leading-tight">{d.title}</div>
                {d.subtitle ? (
                  <p className="mt-3 text-sm opacity-70 leading-relaxed flex-1">{d.subtitle}</p>
                ) : <div className="flex-1" />}

                <div className="mt-7 flex items-center justify-between gap-3">
                  <Link to={`/documents/${d.slug}`}
                        className="text-[11px] uppercase tracking-[0.22em] font-medium inline-flex items-center gap-2"
                        data-testid={`doc-open-${d.slug}`}>
                    Open <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
                  </Link>
                  <PDFDownloadLink
                    document={<EventDocPDF doc={d} />}
                    fileName={`${d.slug}-tl-supplier-summit-2026.pdf`}
                    className="text-[11px] uppercase tracking-[0.22em] inline-flex items-center gap-2 opacity-80 hover:opacity-100"
                    data-testid={`doc-pdf-${d.slug}`}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {({ loading: l }) => (<><Download size={12} /> {l ? "Preparing…" : "PDF"}</>)}
                  </PDFDownloadLink>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </Layout>
  );
}
